import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, Check, Bell, CalendarClock, X, Pause, Play } from 'lucide-react';
import { getRecurring, createRecurring, deleteRecurring, patchRecurring, payRecurring, RecurringResp, moneyEntero as money } from '../api';
import ConceptoSelect from './ConceptoSelect';
import { Skeleton } from '../../../components/Skeleton';
import { useToast } from '../../../context/ToastContext';

const labelCls = 'mb-1 block text-xs font-medium text-content-2';
const inputCls = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';

function RecurringForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [day, setDay] = useState('1');
  const [conceptId, setConceptId] = useState<number | null>(null);   // catálogo (7-oct-2026)
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Ponle nombre'); return; }
    if (!(Number(amount) > 0)) { toast.error('Monto inválido'); return; }
    if (!conceptId) { toast.error('Elige el concepto'); return; }
    setSaving(true);
    try {
      await createRecurring({ name: name.trim(), amount: Number(amount), concept_id: conceptId, day_of_month: Number(day) });
      toast.success('Gasto programado creado'); onSaved();
    } catch (err: any) { toast.error(err?.message || 'No se pudo'); } finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Nuevo gasto programado</h3>
          <button onClick={onClose} className="text-content-3 hover:text-content"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><label className={labelCls}>Concepto *</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Renta del local" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Monto mensual *</label>
              <input type="number" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" /></div>
            <div><label className={labelCls}>Día de pago</label>
              <input type="number" min="1" max="28" className={inputCls} value={day} onChange={(e) => setDay(e.target.value)} /></div>
          </div>
          <div><label className={labelCls}>Concepto del catálogo *</label>
            <ConceptoSelect value={conceptId} onChange={(id) => setConceptId(id)} secciones={['egreso', 'nomina', 'inversion']} className={inputCls} /></div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">{saving ? 'Guardando…' : 'Crear'}</button>
          </div>
        </form>
      </div>
    </div>, document.body);
}

export default function RecurringPanel() {
  const toast = useToast();
  const [resp, setResp] = useState<RecurringResp | null>(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => getRecurring().then(setResp).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  async function pay(id: number, name: string) {
    try { await payRecurring(id); toast.success(`Registrado: ${name}`); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo'); }
  }
  async function toggle(id: number, active: number) {
    try { await patchRecurring(id, { active: active ? 0 : 1 }); load(); } catch (e: any) { toast.error('No se pudo'); }
  }
  async function remove(id: number, name: string) {
    if (!confirm(`¿Borrar "${name}"?`)) return;
    try { await deleteRecurring(id); toast.success('Borrado'); load(); } catch (e: any) { toast.error('No se pudo'); }
  }

  if (!resp) return <Skeleton className="mb-4 h-32 w-full rounded-2xl" />;

  const pending = resp.data.filter((r) => r.active && !r.paid_this_month);

  return (
    <div className="mb-5 rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-1.5 font-display text-base font-bold text-content"><CalendarClock size={17} className="text-brand-blue" /> Gastos programados</h3>
          <p className="text-xs text-content-3">${Math.round(resp.summary.monthly_total).toLocaleString('es-MX')}/mes fijos · te avisamos cuándo toca pagar cada uno.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-1 rounded-lg bg-accent px-2.5 py-1.5 text-xs font-semibold text-on-accent"><Plus size={14} /> Agregar</button>
      </div>

      {/* Recordatorio de pendientes */}
      {pending.length > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3">
          <Bell size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm text-content-2">
            <b className="text-content">{pending.length} pago(s) pendiente(s) este mes</b> — {money(resp.summary.pending_total)} por cubrir.
          </div>
        </div>
      )}

      {resp.data.length === 0 ? (
        <p className="py-2 text-sm text-content-3">Aún no programas gastos fijos. Agrega la renta, nómina, servicios… y te recordamos pagarlos cada mes.</p>
      ) : (
        <div className="divide-y divide-line/60">
          {resp.data.map((r) => (
            <div key={r.id} className={`flex items-center gap-3 py-2.5 ${!r.active ? 'opacity-50' : ''}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-content">{r.name}</div>
                <div className="text-xs text-content-3">{money(r.amount)} · día {r.day_of_month}{r.concept_name ? '' : r.category ? ` · ${r.category}` : ''}</div>
                {/* Concepto del catálogo: se cambia aquí mismo (7-oct-2026). */}
                <ConceptoSelect value={r.concept_id ?? null} secciones={['egreso', 'nomina', 'inversion']} placeholder="Sin concepto: elige uno"
                  className={`mt-1 max-w-full rounded-md border px-1.5 py-0.5 text-[11px] outline-none ${r.concept_id ? 'border-line bg-transparent text-content-3' : 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}
                  onChange={async (id) => { try { await patchRecurring(r.id, { concept_id: id }); load(); } catch (e: any) { toast.error(e?.message || 'No se pudo cambiar el concepto'); } }} />
              </div>
              {r.active && (r.paid_this_month
                ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400"><Check size={12} /> pagado</span>
                : <button onClick={() => pay(r.id, r.name)} className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${r.due ? 'bg-amber-500 text-white' : 'border border-line text-content-2 hover:bg-surface-2'}`}>Registrar pago</button>)}
              <button onClick={() => toggle(r.id, r.active)} className="text-content-3 hover:text-content" title={r.active ? 'Pausar' : 'Activar'}>{r.active ? <Pause size={14} /> : <Play size={14} />}</button>
              <button onClick={() => remove(r.id, r.name)} className="text-content-3 hover:text-red-500" title="Borrar"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {showForm && <RecurringForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
